/**
 * Users & roles (user menu → Users & roles; SUPERADMIN and TENANTADMIN only), 2026-10-06.
 *
 * Users: the tenant's users with their role and last sign-in. An admin adds users with a custom
 * role (a one-time passphrase to hand over, as the superadmin panel does), changes their role,
 * resets their password or removes their access. Nothing is e-mailed unless asked: "e-mail a
 * set-password link" when adding, or "Send link" on a user, and the user picks their own password.
 * Admins themselves (system roles) are listed but managed in the superadmin panel.
 *
 * Roles: a grid of the app's pages × the custom roles. A tick lets the role see the page; one more
 * row lets it approve and dismiss recommendations. Edits collect until "Save changes". A page the
 * app adds later starts unticked for every role.
 *
 * Usage: who uses the system, when and for how long (usage/UsagePanel.tsx; until 2026-10-06 its own
 * page in the user menu, whose address /usage now opens this tab).
 *
 * The API (backend-node lib/team.js) checks all of this again; the page only shows what it allows.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { ApiError } from '@/api/client'
import { PageTitle } from '@/app/shell/PageTitle'
import { absoluteTime, relativeTime } from '@/app/usage/format'
import { UsagePanel } from '@/app/usage/UsagePanel'
import { ActionBadge, Button, DataTable, Modal, StatusTabs, TextInput } from '@/kit'
import { cx } from '@/kit/cx'
import type { Column, DataTableLabels } from '@/kit'
import './i18n'
import { addRole, addUser, changeUserRole, DECIDE, deleteRole, fetchTeam, MAIL_LANGUAGES, removeUser, resetPassword, sendPasswordLink, updateRole } from './teamApi'
import type { LinkSent, Team, TeamRole, TeamUser } from './teamApi'

/** A page of the app, as the grid lists it (from the tenant's extension, in the reader's language). */
export interface TeamPageRef {
    id: string
    title: string
    /** The menu group it sits in, if any (e.g. Analytics). */
    group: string | null
}

type Tab = 'users' | 'roles' | 'usage'
const TABS: readonly Tab[] = ['users', 'roles', 'usage']
type Translate = (key: string, options?: Record<string, unknown>) => string

const SELECT =
    'h-[36px] max-w-full cursor-pointer rounded-[6px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] px-[8px] text-[14px] leading-[20px] text-[var(--text-default)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none'
/** In a table row: the row height leaves 28px. */
const SELECT_INLINE =
    'h-[28px] max-w-full cursor-pointer rounded-[6px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] px-[6px] py-0 text-[14px] leading-[20px] text-[var(--text-default)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none'
/** Small text actions in the grid's role headers. */
const LINK =
    'cursor-pointer rounded-[4px] border-0 bg-transparent p-0 text-[12px] leading-[16px] font-normal text-[var(--text-weak)] underline-offset-2 hover:text-[var(--text-default)] hover:underline focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none'
const CHECKBOX = 'size-[16px] cursor-pointer accent-[var(--text-default)] disabled:cursor-default'

function errorText(t: Translate, error: unknown): string {
    if (error instanceof ApiError && error.code) return t(`team.error.${error.code}`, { defaultValue: t('team.error.generic') })
    return t('team.error.generic')
}

function roleLabel(t: Translate, role: { name: string; system: boolean }): string {
    return role.system ? t(`usage.role.${role.name}`, { defaultValue: role.name }) : role.name
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x))

function Alert({ children }: { children: ReactNode }) {
    return (
        <p role="alert" className="m-0 text-[14px] leading-[20px] font-normal text-[var(--text-danger)]">
            {children}
        </p>
    )
}

function tableLabels(t: Translate, loading: boolean): DataTableLabels {
    return {
        sortAscending: t('team.sortAsc'),
        sortDescending: t('team.sortDesc'),
        columnMenu: (header) => t('team.columnMenu', { header }),
        empty: loading ? t('team.loading') : t('team.empty'),
        loading: t('team.loading'),
    }
}

export function TeamPage({ pages, titleFor }: { pages: TeamPageRef[]; titleFor: (path: string) => string | null }) {
    const { t } = useTranslation()
    const [params, setParams] = useSearchParams()
    const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'users'
    const team = useQuery({ queryKey: ['team'], queryFn: ({ signal }) => fetchTeam(signal) })
    const data = team.data

    return (
        <div className="flex w-full flex-col">
            <PageTitle title={t('team.title')} description={tab === 'usage' ? t('usage.description') : t('team.description')} />
            <div className="w-full px-[var(--layout-page-padding-x)] pt-[16px]">
                <StatusTabs<Tab>
                    ariaLabel={t('team.tabs')}
                    value={tab}
                    onChange={(next) => setParams(next === 'users' ? {} : { tab: next }, { replace: true })}
                    items={[
                        { key: 'users', label: t('team.tab.users'), count: data?.users.length },
                        { key: 'roles', label: t('team.tab.roles'), count: data?.roles.filter((r) => !r.system).length },
                        { key: 'usage', label: t('usage.menu') },
                    ]}
                />
            </div>
            {team.isError && !data && tab !== 'usage' && (
                <div className="flex w-full flex-wrap items-center gap-[16px] gap-y-[8px] px-[var(--layout-page-padding-x)] py-[16px]">
                    <Alert>{t('team.loadFailed')}</Alert>
                    <Button variant="secondary" onClick={() => void team.refetch()}>
                        {t('team.retry')}
                    </Button>
                </div>
            )}
            {tab === 'users' && <UsersPanel team={data} loading={team.isPending} now={team.dataUpdatedAt} />}
            {tab === 'roles' && <RolesPanel team={data} pages={pages} />}
            {tab === 'usage' && <UsagePanel titleFor={titleFor} />}
        </div>
    )
}

// ── Users ────────────────────────────────────────────────────────────────────

interface Secret {
    title: string
    name: string
    email: string
    passphrase: string
    /** Shown above the passphrase, e.g. why the set-password e-mail was not sent. */
    warning?: string
}

/** The UI language if the e-mail has it, else English. */
function mailLanguage(uiLanguage: string): string {
    return MAIL_LANGUAGES.some((l) => l.code === uiLanguage) ? uiLanguage : 'en'
}

function UsersPanel({ team, loading, now }: { team: Team | undefined; loading: boolean; now: number }) {
    const { t, i18n } = useTranslation()
    const client = useQueryClient()
    const refresh = () => client.invalidateQueries({ queryKey: ['team'] })
    const [adding, setAdding] = useState(false)
    const [confirm, setConfirm] = useState<{ kind: 'reset' | 'remove'; user: TeamUser } | null>(null)
    const [secret, setSecret] = useState<Secret | null>(null)
    const [linking, setLinking] = useState<TeamUser | null>(null)
    const [sent, setSent] = useState<{ title: string; text: string } | null>(null)
    const [error, setError] = useState<string | null>(null)
    const customRoles = team?.roles.filter((r) => !r.system) ?? []
    const manageable = (u: TeamUser) => !u.role.system && !u.is_self

    const roleChange = useMutation({
        mutationFn: ({ user, roleId }: { user: TeamUser; roleId: number }) => changeUserRole(user.id, roleId),
        onMutate: () => setError(null),
        onSuccess: refresh,
        onError: (e) => setError(errorText(t, e)),
    })

    const columns: Column<TeamUser>[] = [
        {
            key: 'name',
            header: t('team.col.name'),
            width: 'fill',
            sticky: true,
            render: (u) => (
                <span className="flex min-w-0 items-center gap-[6px]">
                    <span className="truncate font-[550]">{u.name || u.email}</span>
                    {u.is_self && <ActionBadge tone="info">{t('team.you')}</ActionBadge>}
                    {!u.enabled && <ActionBadge tone="warning">{t('team.disabled')}</ActionBadge>}
                </span>
            ),
        },
        { key: 'email', header: t('team.col.email'), width: 260, render: (u) => <span className="block truncate text-[var(--text-weak)]" title={u.email}>{u.email}</span> },
        {
            key: 'role',
            header: t('team.col.role'),
            width: 240,
            render: (u) =>
                manageable(u) ? (
                    <select
                        aria-label={t('team.roleOf', { name: u.name || u.email })}
                        value={String(u.role.id)}
                        disabled={roleChange.isPending}
                        onChange={(event) => roleChange.mutate({ user: u, roleId: Number(event.target.value) })}
                        className={SELECT_INLINE}
                    >
                        {customRoles.map((role) => (
                            <option key={role.id} value={String(role.id)}>
                                {role.name}
                            </option>
                        ))}
                    </select>
                ) : (
                    <span className="flex min-w-0 items-baseline gap-[6px]">
                        <span className="truncate">{roleLabel(t, u.role)}</span>
                        {u.role.system && <span className="truncate text-[12px] leading-[16px] text-[var(--text-weak)]">{t('team.managedByDistribuai')}</span>}
                    </span>
                ),
        },
        {
            key: 'lastLogin',
            header: t('team.col.lastLogin'),
            width: 180,
            render: (u) =>
                u.last_login ? (
                    <span title={absoluteTime(u.last_login, i18n.language)}>{relativeTime(u.last_login, now, i18n.language)}</span>
                ) : (
                    <span className="text-[var(--text-weaker)]">{t('team.never')}</span>
                ),
        },
    ]

    return (
        <>
            <div className="flex w-full flex-wrap items-center gap-[16px] gap-y-[8px] px-[var(--layout-page-padding-x)] py-[16px]">
                <Button variant="primary" disabled={!team || customRoles.length === 0} onClick={() => setAdding(true)}>
                    {t('team.addUser')}
                </Button>
                {team && customRoles.length === 0 && <p className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">{t('team.noCustomRoles')}</p>}
                {error && <Alert>{error}</Alert>}
            </div>
            <DataTable
                columns={columns}
                rows={team?.users ?? []}
                rowKey={(u) => String(u.id)}
                loading={loading}
                labels={tableLabels(t, loading)}
                fillMinWidth={220}
                actionsWidth={400}
                actions={(u) =>
                    manageable(u) ? (
                        <span className="flex items-center gap-[4px]">
                            <Button variant="ghost" onClick={() => setLinking(u)}>
                                {t('team.sendLink')}
                            </Button>
                            <Button variant="ghost" onClick={() => setConfirm({ kind: 'reset', user: u })}>
                                {t('team.resetPassword')}
                            </Button>
                            <Button variant="ghost" onClick={() => setConfirm({ kind: 'remove', user: u })}>
                                {t('team.remove')}
                            </Button>
                        </span>
                    ) : null
                }
            />
            {adding && (
                <AddUserModal
                    roles={customRoles}
                    onClose={() => setAdding(false)}
                    onAdded={(result) => {
                        setAdding(false)
                        void refresh()
                        if (result.link) {
                            setSent({ title: t('team.added.title'), text: t('team.added.linkSent', { email: result.link.email, hours: result.link.expires_hours }) })
                        } else if (result.passphrase) {
                            setSecret({
                                title: t('team.added.title'),
                                name: result.user.name,
                                email: result.user.email,
                                passphrase: result.passphrase,
                                warning: result.mail_error ? t('team.added.mailFailed', { reason: t(`team.error.${result.mail_error}`, { defaultValue: t('team.error.mail_failed') }) }) : undefined,
                            })
                        }
                    }}
                />
            )}
            {confirm && (
                <ConfirmUserModal
                    kind={confirm.kind}
                    user={confirm.user}
                    onClose={() => setConfirm(null)}
                    onDone={(passphrase) => {
                        const user = confirm.user
                        setConfirm(null)
                        if (passphrase) setSecret({ title: t('team.reset.done'), name: user.name || user.email, email: user.email, passphrase })
                        void refresh()
                    }}
                />
            )}
            {secret && <SecretModal secret={secret} onClose={() => setSecret(null)} />}
            {linking && (
                <SendLinkModal
                    user={linking}
                    onClose={() => setLinking(null)}
                    onSent={(link) => {
                        setLinking(null)
                        setSent({ title: t('team.link.title'), text: t('team.link.sent', { email: link.email, hours: link.expires_hours }) })
                    }}
                />
            )}
            {sent && (
                <Modal
                    open
                    onClose={() => setSent(null)}
                    title={sent.title}
                    closeLabel={t('team.close')}
                    footer={
                        <span className="flex items-center">
                            <Button variant="primary" onClick={() => setSent(null)}>
                                {t('team.done')}
                            </Button>
                        </span>
                    }
                >
                    <p role="status" data-testid="team-link-sent" className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">
                        {sent.text}
                    </p>
                </Modal>
            )}
        </>
    )
}

function AddUserModal({
    roles,
    onClose,
    onAdded,
}: {
    roles: TeamRole[]
    onClose: () => void
    onAdded: (result: { user: TeamUser; passphrase?: string; link?: LinkSent; mail_error?: string }) => void
}) {
    const { t, i18n } = useTranslation()
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [roleId, setRoleId] = useState(String(roles[0]?.id ?? ''))
    const [sendLink, setSendLink] = useState(false)
    const [lang, setLang] = useState(mailLanguage(i18n.language))
    const add = useMutation({
        mutationFn: () => addUser({ name: name.trim(), email: email.trim(), role_id: Number(roleId), ...(sendLink ? { send_link: true, lang } : {}) }),
        onSuccess: onAdded,
    })
    const submit = () => {
        if (!add.isPending) add.mutate()
    }
    return (
        <Modal
            open
            onClose={onClose}
            title={t('team.addUser')}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center gap-[8px]">
                    <Button variant="primary" disabled={add.isPending || !name.trim() || !email.trim()} onClick={submit}>
                        {t('team.form.submit')}
                    </Button>
                    <Button onClick={onClose}>{t('team.cancel')}</Button>
                </span>
            }
        >
            <form
                className="flex flex-col gap-[16px]"
                onSubmit={(event) => {
                    event.preventDefault()
                    submit()
                }}
            >
                <TextInput label={t('team.form.name')} value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" maxLength={200} />
                <TextInput label={t('team.form.email')} type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="off" maxLength={255} />
                <label className="flex flex-col gap-[6px] text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                    {t('team.form.role')}
                    <select value={roleId} onChange={(event) => setRoleId(event.target.value)} className={SELECT}>
                        {roles.map((role) => (
                            <option key={role.id} value={String(role.id)}>
                                {role.name}
                            </option>
                        ))}
                    </select>
                </label>
                <label className="flex cursor-pointer items-start gap-[8px] text-[14px] leading-[20px] text-[var(--text-default)]">
                    <input type="checkbox" checked={sendLink} onChange={(event) => setSendLink(event.target.checked)} className={`${CHECKBOX} mt-[2px]`} />
                    {t('team.form.sendLink')}
                </label>
                {sendLink && (
                    <label className="flex flex-col gap-[6px] text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                        {t('team.form.mailLanguage')}
                        <select value={lang} onChange={(event) => setLang(event.target.value)} className={SELECT}>
                            {MAIL_LANGUAGES.map((l) => (
                                <option key={l.code} value={l.code}>
                                    {l.label}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {add.isError && <Alert>{errorText(t, add.error)}</Alert>}
                {/* Enter submits from any field. */}
                <button type="submit" hidden />
            </form>
        </Modal>
    )
}

function ConfirmUserModal({ kind, user, onClose, onDone }: { kind: 'reset' | 'remove'; user: TeamUser; onClose: () => void; onDone: (passphrase: string | null) => void }) {
    const { t } = useTranslation()
    const name = user.name || user.email
    const run = useMutation({
        mutationFn: async () => (kind === 'reset' ? (await resetPassword(user.id)).passphrase : (await removeUser(user.id), null)),
        onSuccess: onDone,
    })
    return (
        <Modal
            open
            onClose={onClose}
            title={t(kind === 'reset' ? 'team.reset.title' : 'team.remove.title')}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center gap-[8px]">
                    <Button variant="primary" disabled={run.isPending} onClick={() => run.mutate()}>
                        {t(kind === 'reset' ? 'team.reset.submit' : 'team.remove.submit')}
                    </Button>
                    <Button onClick={onClose}>{t('team.cancel')}</Button>
                </span>
            }
        >
            <div className="flex flex-col gap-[12px]">
                <p className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">{t(kind === 'reset' ? 'team.reset.confirm' : 'team.remove.confirm', { name })}</p>
                {run.isError && <Alert>{errorText(t, run.error)}</Alert>}
            </div>
        </Modal>
    )
}

/** E-mails a user a link to set their own password, in the language the admin picks. */
function SendLinkModal({ user, onClose, onSent }: { user: TeamUser; onClose: () => void; onSent: (link: LinkSent) => void }) {
    const { t, i18n } = useTranslation()
    const [lang, setLang] = useState(mailLanguage(i18n.language))
    const send = useMutation({ mutationFn: async () => (await sendPasswordLink(user.id, lang)).link, onSuccess: onSent })
    return (
        <Modal
            open
            onClose={onClose}
            title={t('team.link.title')}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center gap-[8px]">
                    <Button variant="primary" disabled={send.isPending} onClick={() => send.mutate()}>
                        {t('team.link.send')}
                    </Button>
                    <Button onClick={onClose}>{t('team.cancel')}</Button>
                </span>
            }
        >
            <div className="flex flex-col gap-[16px]">
                <p className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">{t('team.link.confirm', { email: user.email, name: user.name || user.email })}</p>
                <label className="flex flex-col gap-[6px] text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                    {t('team.form.mailLanguage')}
                    <select value={lang} onChange={(event) => setLang(event.target.value)} className={SELECT}>
                        {MAIL_LANGUAGES.map((l) => (
                            <option key={l.code} value={l.code}>
                                {l.label}
                            </option>
                        ))}
                    </select>
                </label>
                {send.isError && <Alert>{errorText(t, send.error)}</Alert>}
            </div>
        </Modal>
    )
}

function SecretModal({ secret, onClose }: { secret: Secret; onClose: () => void }) {
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(secret.passphrase)
            setCopied(true)
        } catch {
            setCopied(false)
        }
    }
    return (
        <Modal
            open
            onClose={onClose}
            title={secret.title}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center">
                    <Button variant="primary" onClick={onClose}>
                        {t('team.done')}
                    </Button>
                </span>
            }
        >
            <div className="flex flex-col gap-[12px]">
                {secret.warning && <Alert>{secret.warning}</Alert>}
                <p className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">{t('team.passphrase.intro', { name: secret.name })}</p>
                <div className="flex flex-wrap items-center gap-[12px]">
                    <code data-testid="team-passphrase" className="rounded-[6px] border border-solid border-[var(--border-default)] bg-[var(--surface-page)] px-[12px] py-[8px] text-[16px] leading-[24px] font-[550] tracking-[0.02em] text-[var(--text-default)] select-all">
                        {secret.passphrase}
                    </code>
                    <Button onClick={() => void copy()}>{copied ? t('team.copied') : t('team.copy')}</Button>
                </div>
                <p className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">{t('team.passphrase.signIn', { email: secret.email })}</p>
            </div>
        </Modal>
    )
}

// ── Roles ────────────────────────────────────────────────────────────────────
//
// A list of the roles and, beside it, the chosen role's pages as a checklist in the menu's groups:
// it stays readable with 15 roles or 50 (a role × page grid did not). Edits are kept per role,
// marked in the list, until "Save changes".

const SECTION = 'm-0 text-[12px] leading-[16px] font-[550] tracking-[0.04em] text-[var(--text-weak)] uppercase'
const ROLE_ITEM =
    'flex w-full cursor-pointer flex-col items-start gap-[2px] rounded-[6px] border-0 bg-transparent px-[12px] py-[8px] text-left hover:bg-[var(--interaction-hover)] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none'
const CHECK_ROW = 'flex cursor-pointer items-center gap-[8px] rounded-[6px] px-[8px] py-[6px] text-[14px] leading-[20px] text-[var(--text-default)] hover:bg-[var(--interaction-hover)]'

function RolesPanel({ team, pages }: { team: Team | undefined; pages: TeamPageRef[] }) {
    const { t } = useTranslation()
    const client = useQueryClient()
    const [params, setParams] = useSearchParams()
    const refresh = () => client.invalidateQueries({ queryKey: ['team'] })
    const roles = team?.roles.filter((r) => !r.system) ?? []
    const selected = roles.find((r) => String(r.id) === params.get('role')) ?? roles[0] ?? null
    const select = (roleId: number) => {
        const next = new URLSearchParams(params)
        next.set('role', String(roleId))
        setParams(next, { replace: true })
    }
    const [draft, setDraft] = useState<Record<number, string[]>>({})
    const [saved, setSaved] = useState(false)
    const [naming, setNaming] = useState<{ role: TeamRole | null } | null>(null)
    const [deleting, setDeleting] = useState<TeamRole | null>(null)

    const pageKeys = pages.map((page) => `page:${page.id}`)
    const granted = (role: TeamRole) => draft[role.id] ?? role.permissions
    const isDirty = (role: TeamRole) => draft[role.id] !== undefined && !sameSet(draft[role.id] ?? [], role.permissions)
    const dirty = roles.filter(isDirty)
    const setGranted = (role: TeamRole, next: Set<string>) => {
        setSaved(false)
        setDraft((current) => ({ ...current, [role.id]: [...next].sort() }))
    }
    const toggle = (role: TeamRole, key: string) => {
        const next = new Set(granted(role))
        if (next.has(key)) next.delete(key)
        else next.add(key)
        setGranted(role, next)
    }
    const setAllPages = (role: TeamRole, on: boolean) => {
        const next = new Set(granted(role))
        for (const key of pageKeys) {
            if (on) next.add(key)
            else next.delete(key)
        }
        setGranted(role, next)
    }
    const pagesOn = (role: TeamRole) => pageKeys.filter((key) => granted(role).includes(key)).length

    const save = useMutation({
        mutationFn: async () => {
            for (const role of dirty) await updateRole(role.id, { permissions: draft[role.id] ?? role.permissions })
        },
        onSuccess: async () => {
            await refresh()
            setDraft({})
            setSaved(true)
        },
    })

    // The pages in the menu's groups, in menu order (ungrouped pages are the top-level items).
    const groups: { label: string | null; pages: TeamPageRef[] }[] = []
    for (const page of pages) {
        const group = groups.find((g) => g.label === page.group)
        if (group) group.pages.push(page)
        else groups.push({ label: page.group, pages: [page] })
    }

    return (
        <>
            <div className="flex w-full flex-wrap items-center gap-[16px] gap-y-[8px] px-[var(--layout-page-padding-x)] py-[16px]">
                <Button variant="primary" disabled={!team} onClick={() => setNaming({ role: null })}>
                    {t('team.addRole')}
                </Button>
                <Button disabled={dirty.length === 0 || save.isPending} onClick={() => save.mutate()}>
                    {t('team.roles.save')}
                </Button>
                {dirty.length > 0 && (
                    <Button variant="ghost" disabled={save.isPending} onClick={() => setDraft({})}>
                        {t('team.roles.discard')}
                    </Button>
                )}
                {saved && dirty.length === 0 && (
                    <p role="status" className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">
                        {t('team.roles.saved')}
                    </p>
                )}
                {save.isError && <Alert>{errorText(t, save.error)}</Alert>}
            </div>
            <p className="m-0 px-[var(--layout-page-padding-x)] pb-[16px] text-[14px] leading-[20px] text-[var(--text-weak)]">{team && roles.length === 0 ? t('team.roles.empty') : t('team.roles.intro')}</p>
            {team && selected && (
                <div className="grid w-full grid-cols-[280px_minmax(0,1fr)] items-start gap-[24px] px-[var(--layout-page-padding-x)] pb-[32px] max-md:grid-cols-1">
                    <nav aria-label={t('team.tab.roles')} className="flex max-h-[70vh] flex-col gap-[2px] overflow-y-auto rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] p-[4px] max-md:max-h-[280px]">
                        {roles.map((role) => (
                            <button
                                key={role.id}
                                type="button"
                                aria-current={role.id === selected.id ? 'true' : undefined}
                                onClick={() => select(role.id)}
                                className={cx(ROLE_ITEM, role.id === selected.id && 'bg-[var(--interaction-active)] hover:bg-[var(--interaction-active)]')}
                            >
                                <span className="flex max-w-full min-w-0 items-center gap-[6px]">
                                    <span className="truncate text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">{role.name}</span>
                                    {isDirty(role) && <span role="img" aria-label={t('team.roles.unsaved')} title={t('team.roles.unsaved')} className="size-[8px] shrink-0 rounded-full bg-[var(--text-warning)]" />}
                                </span>
                                <span className="text-[12px] leading-[16px] text-[var(--text-weak)]">
                                    {t('team.roles.users', { count: role.users })} · {t('team.roles.pagesCount', { count: pagesOn(role), total: pageKeys.length })}
                                </span>
                            </button>
                        ))}
                    </nav>
                    <section aria-labelledby="team-role-name" className="flex min-w-0 flex-col gap-[20px] rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-default)] px-[20px] py-[16px]">
                        <header className="flex flex-wrap items-baseline justify-between gap-[12px]">
                            <h2 id="team-role-name" className="m-0 text-[20px] leading-[28px] font-[550] text-[var(--text-default)]">
                                {selected.name}
                            </h2>
                            <span className="flex items-center gap-[16px]">
                                <button type="button" className={LINK} onClick={() => setNaming({ role: selected })}>
                                    {t('team.roles.rename')}
                                </button>
                                <button type="button" className={LINK} onClick={() => setDeleting(selected)}>
                                    {t('team.roles.delete')}
                                </button>
                            </span>
                        </header>
                        <div className="flex flex-col gap-[4px]">
                            <div className="flex flex-wrap items-center justify-between gap-[12px]">
                                <h3 className={SECTION}>{t('team.roles.pages')}</h3>
                                <span className="flex items-center gap-[16px]">
                                    <button type="button" className={LINK} onClick={() => setAllPages(selected, true)}>
                                        {t('team.roles.all')}
                                    </button>
                                    <button type="button" className={LINK} onClick={() => setAllPages(selected, false)}>
                                        {t('team.roles.none')}
                                    </button>
                                </span>
                            </div>
                            {groups.map((group) => (
                                <fieldset key={group.label ?? '—'} className="m-0 min-w-0 border-0 p-0">
                                    {group.label && <legend className="mt-[12px] mb-[2px] px-[8px] text-[14px] leading-[20px] font-[550] text-[var(--text-weak)]">{group.label}</legend>}
                                    <div className="grid grid-cols-2 gap-x-[24px] max-lg:grid-cols-1">
                                        {group.pages.map((page) => (
                                            <label key={page.id} className={CHECK_ROW}>
                                                <input type="checkbox" className={CHECKBOX} checked={granted(selected).includes(`page:${page.id}`)} onChange={() => toggle(selected, `page:${page.id}`)} />
                                                <span className="min-w-0">{page.title}</span>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                            ))}
                        </div>
                        <div className="flex flex-col gap-[4px] border-t border-solid border-[var(--border-weak)] pt-[16px]">
                            <h3 className={SECTION}>{t('team.roles.actions')}</h3>
                            <label className={cx(CHECK_ROW, 'items-start')}>
                                <input type="checkbox" className={cx(CHECKBOX, 'mt-[2px]')} checked={granted(selected).includes(DECIDE)} onChange={() => toggle(selected, DECIDE)} />
                                <span className="flex min-w-0 flex-col">
                                    <span>{t('team.roles.decide')}</span>
                                    <span className="text-[12px] leading-[16px] text-[var(--text-weak)]">{t('team.roles.decideHint')}</span>
                                </span>
                            </label>
                        </div>
                    </section>
                </div>
            )}
            {naming && (
                <RoleNameModal
                    role={naming.role}
                    roles={roles}
                    onClose={() => setNaming(null)}
                    onDone={(roleId) => {
                        setNaming(null)
                        void refresh()
                        if (roleId) select(roleId)
                    }}
                />
            )}
            {deleting && (
                <DeleteRoleModal
                    role={deleting}
                    onClose={() => setDeleting(null)}
                    onDone={() => {
                        setDeleting(null)
                        void refresh()
                    }}
                />
            )}
        </>
    )
}

/** Add a role (optionally with another role's permissions to start from) or rename one. */
function RoleNameModal({ role, roles, onClose, onDone }: { role: TeamRole | null; roles: TeamRole[]; onClose: () => void; onDone: (roleId: number | null) => void }) {
    const { t } = useTranslation()
    const [name, setName] = useState(role?.name ?? '')
    const [copyFrom, setCopyFrom] = useState('')
    const run = useMutation({
        mutationFn: async () => {
            if (role) {
                await updateRole(role.id, { name })
                return null
            }
            const source = roles.find((r) => String(r.id) === copyFrom)
            return (await addRole(name, source ? source.permissions : [])).id
        },
        onSuccess: onDone,
    })
    const submit = () => {
        if (!run.isPending && name.trim()) run.mutate()
    }
    return (
        <Modal
            open
            onClose={onClose}
            title={t(role ? 'team.role.renameTitle' : 'team.role.addTitle')}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center gap-[8px]">
                    <Button variant="primary" disabled={run.isPending || !name.trim()} onClick={submit}>
                        {t('team.save')}
                    </Button>
                    <Button onClick={onClose}>{t('team.cancel')}</Button>
                </span>
            }
        >
            <form
                className="flex flex-col gap-[12px]"
                onSubmit={(event) => {
                    event.preventDefault()
                    submit()
                }}
            >
                <div className="flex flex-col gap-[6px]">
                    <TextInput label={t('team.role.name')} value={name} onChange={(event) => setName(event.target.value.toUpperCase())} autoComplete="off" maxLength={50} />
                    <p className="m-0 text-[14px] leading-[20px] text-[var(--text-weak)]">{t('team.role.hint')}</p>
                </div>
                {!role && roles.length > 0 && (
                    <label className="flex flex-col gap-[6px] text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">
                        {t('team.role.startFrom')}
                        <select value={copyFrom} onChange={(event) => setCopyFrom(event.target.value)} className={SELECT}>
                            <option value="">{t('team.role.startEmpty')}</option>
                            {roles.map((r) => (
                                <option key={r.id} value={String(r.id)}>
                                    {t('team.role.copyOf', { role: r.name })}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {run.isError && <Alert>{errorText(t, run.error)}</Alert>}
                <button type="submit" hidden />
            </form>
        </Modal>
    )
}

function DeleteRoleModal({ role, onClose, onDone }: { role: TeamRole; onClose: () => void; onDone: () => void }) {
    const { t } = useTranslation()
    const run = useMutation({ mutationFn: () => deleteRole(role.id), onSuccess: onDone })
    return (
        <Modal
            open
            onClose={onClose}
            title={t('team.role.deleteTitle')}
            closeLabel={t('team.close')}
            footer={
                <span className="flex items-center gap-[8px]">
                    <Button variant="primary" disabled={run.isPending} onClick={() => run.mutate()}>
                        {t('team.roles.delete')}
                    </Button>
                    <Button onClick={onClose}>{t('team.cancel')}</Button>
                </span>
            }
        >
            <div className="flex flex-col gap-[12px]">
                <p className="m-0 text-[14px] leading-[20px] text-[var(--text-default)]">{t('team.role.deleteConfirm', { role: role.name })}</p>
                {run.isError && <Alert>{errorText(t, run.error)}</Alert>}
            </div>
        </Modal>
    )
}

